import { editsOf, value } from '../../parser/fortigate'
import type { FgConfig } from '../../parser/types'
import { finding, type Finding, type Rule } from '../types'
import { policies, type PolicyView } from './policy'

/**
 * Firmware age and whether traffic crossing the perimeter is actually inspected.
 *
 * These checks are deliberately scoped to policies that touch a WAN-role interface.
 * Demanding IPS and antivirus on an internal-to-internal rule would be noise, and a
 * finding an engineer knows is wrong costs more than the finding is worth.
 */

/** Names of interfaces the config itself marks as WAN. */
function wanInterfaces(config: FgConfig): Set<string> {
  const names = new Set<string>()
  for (const iface of editsOf(config, 'system', 'interface')) {
    if (value(iface, 'role') === 'wan') names.add(iface.key.toLowerCase())
  }
  return names
}

const touchesWan = (policy: PolicyView, wan: Set<string>): boolean =>
  policy.srcintf.some((i) => wan.has(i)) || policy.dstintf.some((i) => wan.has(i))

const label = (p: PolicyView): string => (p.name !== undefined ? `${p.key} ("${p.name}")` : p.key)

/**
 * True when a profile field is genuinely set.
 *
 * `show full-configuration` prints every field including the unset ones, and FortiOS
 * writes an unset profile as `set ips-sensor ''`. Testing for the key's presence therefore
 * passes a policy whose IPS is blank — which is the worst kind of miss, because such a
 * policy has utm-status enabled and other profiles attached, so it reads as inspected in
 * a review while one engine is doing nothing.
 */
function hasProfile(sets: Record<string, string[]> | undefined, key: string): boolean {
  const parts = sets?.[key]
  if (parts === undefined) return false
  const joined = parts.join(' ').trim()
  return joined !== '' && joined !== 'none'
}

export const endOfLifeFirmware: Rule = {
  id: 'fg-eol-firmware',
  title: 'Firmware major version no longer supported',
  run(config) {
    const { major, version, model } = config.device
    if (major === undefined || major >= 7) return []

    return [
      finding(endOfLifeFirmware, {
        severity: 'critical',
        title: `FortiOS ${version} is past end of support`,
        detail:
          `The configuration header reports ${model ?? 'this device'} running FortiOS ${version}. ` +
          'FortiOS 5.x and 6.x no longer receive security fixes, so every vulnerability disclosed ' +
          'since that branch closed remains open on this device — and firewall vulnerabilities are ' +
          'exploited from the internet within days of disclosure.',
        location: 'config-version header',
        line: 1,
        // No PCI citation: the patching requirement's sub-number is unverified.
        remediation:
          '# Check the supported upgrade path for your model in the Fortinet upgrade tool\n' +
          '# before flashing. Moving across major versions usually requires intermediate\n' +
          '# steps, and skipping one can leave the device unbootable.',
      }),
    ]
  },
}

export const agingFirmware: Rule = {
  id: 'fg-aging-firmware',
  title: 'Firmware branch may be near end of support',
  run(config) {
    const { major, minor, version } = config.device
    if (major !== 7 || minor === undefined || minor > 2) return []

    return [
      finding(agingFirmware, {
        severity: 'info',
        title: `FortiOS ${version} is an older 7.x branch`,
        detail:
          `This device reports FortiOS ${version}. Fortinet supports each branch for a fixed window ` +
          'and the older 7.x branches reach end of engineering and end of support well before the ' +
          'current ones. Confirm this branch is still supported before your next review cycle.',
        location: 'config-version header',
        line: 1,
        remediation:
          '# Check the current status of this branch on Fortinet\'s product lifecycle page,\n' +
          '# then plan an upgrade if it is past end of engineering support.',
      }),
    ]
  },
}

export const perimeterPolicyWithoutInspection: Rule = {
  id: 'fg-policy-no-inspection',
  title: 'Perimeter policy without security inspection',
  run(config) {
    const wan = wanInterfaces(config)
    if (wan.size === 0) return [] // Cannot tell which policies cross the perimeter.

    const out: Finding[] = []
    const raw = editsOf(config, 'firewall', 'policy')

    for (const [index, policy] of policies(config).entries()) {
      if (!policy.enabled || policy.action !== 'accept') continue
      if (!touchesWan(policy, wan)) continue

      const entry = raw[index]
      const missing: string[] = []
      if (!hasProfile(entry?.sets, 'ips-sensor')) missing.push('IPS')
      if (!hasProfile(entry?.sets, 'av-profile')) missing.push('antivirus')
      if (!hasProfile(entry?.sets, 'application-list')) missing.push('application control')
      if (missing.length === 0) continue

      out.push(
        finding(perimeterPolicyWithoutInspection, {
          severity: missing.length >= 3 ? 'medium' : 'low',
          title: `Policy ${label(policy)} crosses the perimeter without ${missing.join(', ')}`,
          detail:
            `Policy ${policy.key} accepts traffic between ${policy.srcintf.join(', ')} and ` +
            `${policy.dstintf.join(', ')}, one of which is a WAN interface, but has no ` +
            `${missing.join(' profile, no ')} profile attached. Traffic it permits is forwarded ` +
            'without being examined for known exploits or malware.',
          location: `firewall policy ${policy.key}`,
          line: policy.line,
          remediation:
            `config firewall policy\n    edit ${policy.key}\n` +
            '        set utm-status enable\n        set ips-sensor "default"\n' +
            '        set av-profile "default"\n        set application-list "default"\n    next\nend',
        }),
      )
    }

    return out
  },
}

export const utmDisabledOnPerimeter: Rule = {
  id: 'fg-policy-utm-disabled',
  title: 'Security profiles configured but switched off',
  run(config) {
    const wan = wanInterfaces(config)
    const out: Finding[] = []
    const raw = editsOf(config, 'firewall', 'policy')

    for (const [index, policy] of policies(config).entries()) {
      if (!policy.enabled || policy.action !== 'accept') continue
      const entry = raw[index]
      if (value(entry, 'utm-status') !== 'disable') continue
      // Only meaningful if a profile was actually attached, or the policy crosses the edge.
      const anyProfile = ['ips-sensor', 'av-profile', 'webfilter-profile', 'application-list'].some(
        (key) => hasProfile(entry?.sets, key),
      )
      if (!anyProfile && !touchesWan(policy, wan)) continue

      out.push(
        finding(utmDisabledOnPerimeter, {
          severity: 'medium',
          title: `Policy ${label(policy)} has inspection switched off`,
          detail:
            `Policy ${policy.key} sets utm-status to disable. Any security profile named on this ` +
            'policy is configured but never applied, which reads as protected in a review while ' +
            'inspecting nothing.',
          location: `firewall policy ${policy.key}`,
          line: policy.line,
          remediation:
            `config firewall policy\n    edit ${policy.key}\n        set utm-status enable\n    next\nend`,
        }),
      )
    }

    return out
  },
}

export const undocumentedPolicies: Rule = {
  id: 'fg-policy-undocumented',
  title: 'Policies with no description',
  run(config) {
    const raw = editsOf(config, 'firewall', 'policy')
    if (raw.length === 0) return []

    const undocumented = raw.filter((p) => {
      const name = p.sets['name']?.join(' ').trim()
      const comments = (p.sets['comments'] ?? p.sets['comment'])?.join(' ').trim()
      return (name === undefined || name === '') && (comments === undefined || comments === '')
    })

    // A couple of unnamed rules is normal. A ruleset that is mostly unnamed is the
    // condition that makes the six-month review impossible to do honestly.
    if (undocumented.length < 3 || undocumented.length / raw.length < 0.3) return []

    return [
      finding(undocumentedPolicies, {
        severity: 'low',
        title: `${undocumented.length} of ${raw.length} policies have neither a name nor a comment`,
        detail:
          `Policies ${undocumented.slice(0, 8).map((p) => p.key).join(', ')}` +
          (undocumented.length > 8 ? ' and others' : '') +
          ' carry no name and no comment. A reviewer cannot confirm that a rule is still needed ' +
          'without knowing why it was added, so undocumented rules survive every review by default.',
        location: 'firewall policy',
        pci: '1.2.7',
        remediation:
          'config firewall policy\n    edit <id>\n' +
          '        set name "purpose - requester - ticket"\n' +
          '        set comments "why this exists and when to revisit"\n    next\nend',
      }),
    ]
  },
}

export const noAutomaticUpdates: Rule = {
  id: 'fg-no-fortiguard-updates',
  title: 'Automatic threat definition updates not enabled',
  run(config) {
    const autoupdate = editsOf(config, 'system', 'autoupdate', 'schedule')
    const section = config.sections.find(
      (s) => s.path.join(' ') === 'system autoupdate schedule',
    )
    if (section === undefined && autoupdate.length === 0) return []

    const status = value(section, 'status')
    const frequency = value(section, 'frequency')
    if (status === 'enable' && frequency !== undefined && frequency !== 'never') return []

    return [
      finding(noAutomaticUpdates, {
        severity: 'high',
        title: 'Threat definitions are not updated automatically',
        detail:
          'system autoupdate schedule is disabled or set to never. Antivirus and IPS signatures ' +
          'go stale from the day they were last pulled, so the inspection profiles on every policy ' +
          'are checking against a snapshot of the threat landscape rather than the current one.',
        location: 'system autoupdate schedule',
        line: section?.line,
        // No PCI citation: the anti-malware currency sub-number is unverified.
        remediation:
          'config system autoupdate schedule\n    set status enable\n    set frequency automatic\nend',
      }),
    ]
  },
}

export const interfaceWithoutRole: Rule = {
  id: 'fg-interface-no-role',
  title: 'Interfaces with no role assigned',
  run(config) {
    const interfaces = editsOf(config, 'system', 'interface')
    const undefinedRole = interfaces.filter((i) => {
      const role = value(i, 'role')
      return role === undefined || role === 'undefined'
    })

    if (undefinedRole.length === 0 || interfaces.length === 0) return []

    return [
      finding(interfaceWithoutRole, {
        severity: 'info',
        title: `${undefinedRole.length} of ${interfaces.length} interfaces have no role set`,
        detail:
          `Interfaces ${undefinedRole.slice(0, 8).map((i) => i.key).join(', ')}` +
          (undefinedRole.length > 8 ? ' and others' : '') +
          ' have no role. FortiOS uses the role to decide which settings to surface, and any review ' +
          'that reasons about "the WAN side" — including several checks in this report — has to ' +
          'guess when the role is missing.',
        location: 'system interface',
        remediation:
          'config system interface\n    edit "<name>"\n        set role lan\n    next\nend',
      }),
    ]
  },
}

export const PROFILE_RULES: Rule[] = [
  endOfLifeFirmware,
  agingFirmware,
  perimeterPolicyWithoutInspection,
  utmDisabledOnPerimeter,
  undocumentedPolicies,
  noAutomaticUpdates,
  interfaceWithoutRole,
]
